import { baseApi } from "./baseApi";
import type { WorkItemApprover } from "./portalsApi";

// ──────────────────────────── Types ────────────────────────────

export interface CustomerSlaEntry {
  id: string;
  metric: string;
  status: "active" | "paused" | "met" | "warning" | "breached";
  targetMinutes: number;
  elapsedMinutes: number;
  dueAt: string | null;
  completedAt: string | null;
}

export interface CustomerPortal {
  id: string;
  instanceId: string;
  key: string;
  name: string;
  description: string | null;
  color: string;
  logoUrl: string | null;
  isPrivate: boolean;
  requestTypeCount: string;
  currentUserCanReadAll: boolean;
  /** #961: caller's portal role grants create (false for read-only Viewer). */
  currentUserCanCreate: boolean;
}

export interface CustomerRequestType {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  portalGroupId: string | null;
  portalGroupName: string | null;
  position: number;
  formSchema: Record<string, unknown>[] | null;
  formId: string | null;
}

export interface CustomerCustomField {
  id: string;
  fieldKey: string;
  name: string;
  description: string | null;
  fieldType:
    | "text"
    | "number"
    | "date"
    | "select"
    | "multiselect"
    | "checkbox"
    | "url"
    | "user_picker"
    | "file";
  isRequired: boolean;
  defaultValue: unknown;
  options: string[];
  validation: Record<string, unknown>;
  position: number;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerRequest {
  id: string;
  itemKey: string;
  title: string;
  description: string | null;
  priority: string;
  statusName: string | null;
  statusColor: string | null;
  statusCategory: string | null;
  requestTypeId: string | null;
  requestTypeName: string | null;
  portalGroupId: string | null;
  portalGroupName: string | null;
  workType: string | null;
  reporterName: string | null;
  reporterEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerWatcher {
  id: string;
  name: string;
  email: string;
}

export interface CustomerHistoryEntry {
  id: string;
  actorId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  action: string;
  fieldName: string | null;
  previousValue: string | null;
  newValue: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface CustomerRequestWithPortal extends CustomerRequest {
  portalId: string;
  // The cross-portal list LEFT JOINs `portals`, so every field taken from that
  // row is null for a work item whose portal is gone. Typed honestly because a
  // missing `portalKey` is what crashed /employee/requests on QA: `generatePath`
  // throws on an absent param instead of returning a partial path.
  portalName: string | null;
  portalKey: string | null;
  portalColor: string | null;
}

export interface CustomerRequestStats {
  total: number;
  open: number;
  closed: number;
}

export interface ApprovalRequestStats {
  total: number;
  open: number;
  closed: number;
  needsApproval: number;
  approved: number;
}

export interface ApprovalRequestWithPortal extends CustomerRequestWithPortal {
  approvalStatus: string;
  approvalRespondedAt: string | null;
}

export interface CustomerPortalApprover {
  id: string;
  agentId: string;
  role: string;
  isApprover: boolean;
  agentName: string | null;
  agentEmail: string | null;
}

export interface PortalBranding {
  id: string;
  instanceId: string;
  heroColor: string;
  heroTitle: string;
  heroSubtitle: string | null;
  logoUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerPortalGroup {
  id: string;
  portalId: string;
  name: string;
  description: string | null;
  position: number;
  requestTypeCount: number;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ──────────────────────────── API ────────────────────────────

const customerPortalApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCustomerPortals: builder.query<{ data: CustomerPortal[] }, { search?: string } | void>({
      query: (args) => ({
        url: "/customer/portals",
        params: args ? { search: args.search } : undefined,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((p) => ({ type: "Portal" as const, id: `CUSTOMER_${p.id}` })),
              { type: "Portal", id: "CUSTOMER_LIST" },
            ]
          : [{ type: "Portal", id: "CUSTOMER_LIST" }],
    }),

    getCustomerPortal: builder.query<{ data: CustomerPortal }, { portalKey: string }>({
      query: ({ portalKey }) => `/customer/portals/${portalKey}`,
      providesTags: (_result, _err, { portalKey }) => [
        { type: "Portal", id: `CUSTOMER_${portalKey}` },
      ],
    }),

    getCustomerPortalGroups: builder.query<{ data: CustomerPortalGroup[] }, { portalKey: string }>({
      query: ({ portalKey }) => `/customer/portals/${portalKey}/groups`,
      providesTags: (_result, _err, { portalKey }) => [
        { type: "PortalGroup", id: `CUSTOMER_${portalKey}` },
      ],
    }),

    getCustomerRequestTypes: builder.query<{ data: CustomerRequestType[] }, { portalKey: string }>({
      query: ({ portalKey }) => `/customer/portals/${portalKey}/request-types`,
      providesTags: (_result, _err, { portalKey }) => [
        { type: "RequestType", id: `CUSTOMER_${portalKey}` },
      ],
    }),

    getCustomerCustomFields: builder.query<
      { data: CustomerCustomField[]; total: number },
      { portalKey: string }
    >({
      query: ({ portalKey }) => `/customer/portals/${portalKey}/custom-fields`,
      providesTags: (_result, _err, { portalKey }) => [
        { type: "CustomField", id: `CUSTOMER_${portalKey}` },
      ],
    }),

    getCustomerRequests: builder.query<
      { data: CustomerRequest[]; pagination: PaginationMeta },
      {
        portalKey: string;
        page?: number;
        limit?: number;
        search?: string;
        status?: "open" | "closed" | "all";
        reporterOnly?: boolean;
      }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/customer/portals/${portalKey}/requests`,
        params,
      }),
      providesTags: (_result, _err, { portalKey }) => [
        { type: "WorkItem", id: `CUSTOMER_${portalKey}` },
      ],
    }),

    getAllCustomerRequests: builder.query<
      {
        data: CustomerRequestWithPortal[];
        pagination: PaginationMeta;
        stats: CustomerRequestStats;
      },
      {
        page?: number;
        limit?: number;
        search?: string;
        status?: "open" | "closed" | "all";
        portalId?: string;
        reporterOnly?: boolean;
      } | void
    >({
      query: (args) => ({
        url: "/customer/requests",
        params: args || undefined,
      }),
      providesTags: [{ type: "WorkItem", id: "CUSTOMER_ALL_LIST" }],
    }),

    getApprovalRequests: builder.query<
      {
        data: ApprovalRequestWithPortal[];
        pagination: PaginationMeta;
        stats: ApprovalRequestStats;
        isApprover: boolean;
      },
      {
        page?: number;
        limit?: number;
        search?: string;
        approvalStatus?: "pending" | "approved" | "all";
        portalId?: string;
      } | void
    >({
      query: (args) => ({
        url: "/customer/approval-requests",
        params: args || undefined,
      }),
      providesTags: [{ type: "Approval", id: "CUSTOMER_APPROVAL_LIST" }],
    }),

    getCustomerPortalApprovers: builder.query<
      { data: CustomerPortalApprover[] },
      { portalKey: string }
    >({
      query: ({ portalKey }) => `/customer/portals/${portalKey}/approvers`,
      providesTags: (_result, _err, { portalKey }) => [
        { type: "PortalMember", id: `CUSTOMER_APPROVERS_${portalKey}` },
      ],
    }),

    // Unlinked attachment upload/delete (for request creation flow)
    uploadCustomerAttachment: builder.mutation<
      {
        data: {
          id: string;
          fileName: string;
          fileSize: number;
          contentType: string;
          createdAt: string;
        };
      },
      { portalKey: string; file: File }
    >({
      query: ({ portalKey, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/customer/portals/${portalKey}/upload-attachment`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [{ type: "WorkItem", id: "CUSTOMER_ALL_LIST" }],
    }),

    deleteCustomerAttachment: builder.mutation<
      { success: boolean },
      { portalKey: string; attachmentId: string }
    >({
      query: ({ portalKey, attachmentId }) => ({
        url: `/customer/portals/${portalKey}/upload-attachment/${attachmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "WorkItem", id: "CUSTOMER_ALL_LIST" }],
    }),

    deleteCustomerTicketAttachment: builder.mutation<
      { success: boolean },
      { portalKey: string; itemKey: string; attachmentId: string }
    >({
      query: ({ portalKey, itemKey, attachmentId }) => ({
        url: `/customer/portals/${portalKey}/requests/${itemKey}/attachments/${attachmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _err, { portalKey, itemKey }) => [
        { type: "WorkItem", id: `CUSTOMER_DETAIL_${portalKey}_${itemKey}` },
      ],
    }),

    createCustomerRequest: builder.mutation<
      { data: { id: string; itemKey: string } },
      {
        portalKey: string;
        requestTypeId: string;
        title: string;
        description?: string | null;
        priority?: string;
        approverIds?: string[];
        watcherIds?: string[];
        attachmentIds?: string[];
        customFields?: Record<string, unknown>;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/customer/portals/${portalKey}/requests`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _err, { portalKey }) => [
        { type: "WorkItem", id: `CUSTOMER_${portalKey}` },
        { type: "WorkItem", id: "CUSTOMER_ALL_LIST" },
      ],
    }),

    setWorkItemApprovers: builder.mutation<
      { data: WorkItemApprover[] },
      { portalKey: string; itemKey: string; approverIds: string[] }
    >({
      query: ({ portalKey, itemKey, ...body }) => ({
        url: `/customer/portals/${portalKey}/requests/${itemKey}/approvers`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _err, { portalKey, itemKey }) => [
        { type: "Approval", id: "CUSTOMER_APPROVAL_LIST" },
        { type: "WorkItem", id: `APPROVERS_${itemKey}` },
        { type: "WorkItem", id: `CUSTOMER_DETAIL_${portalKey}_${itemKey}` },
      ],
    }),

    getPortalBranding: builder.query<{ data: PortalBranding }, void>({
      query: () => "/customer/portal-settings",
      providesTags: [{ type: "Portal", id: "BRANDING" }],
    }),

    updatePortalBranding: builder.mutation<
      { data: PortalBranding },
      Partial<Omit<PortalBranding, "id" | "instanceId" | "createdAt" | "updatedAt">>
    >({
      query: (body) => ({
        url: "/customer/portal-settings",
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "Portal", id: "BRANDING" }],
    }),

    uploadPortalBrandingLogo: builder.mutation<{ data: PortalBranding }, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: "/customer/portal-settings/logo",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [{ type: "Portal", id: "BRANDING" }],
    }),

    deletePortalBrandingLogo: builder.mutation<{ data: PortalBranding }, void>({
      query: () => ({
        url: "/customer/portal-settings/logo",
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Portal", id: "BRANDING" }],
    }),

    // ──────────────── Request Detail & Comments ────────────────

    getCustomerRequestDetail: builder.query<
      {
        data:
          | (CustomerRequest & {
              currentAgentId?: string | null;
              isCurrentUserReporter?: boolean;
              isCurrentUserAssignee?: boolean;
              isCurrentUserWatcher?: boolean;
              canEditTitle?: boolean;
              canEditDescription?: boolean;
              /** #948: whether this caller may post a comment on this request. */
              canComment?: boolean;
              sla?: CustomerSlaEntry[];
              comments: {
                id: string;
                authorId: string;
                body: string;
                htmlBody: string | null;
                authorName: string | null;
                authorEmail: string | null;
                visibility: "public" | "internal";
                metadata?: {
                  source?: string;
                  agentId?: string;
                  agentName?: string;
                  [key: string]: unknown;
                } | null;
                createdAt: string;
                canEdit?: boolean;
                canDelete?: boolean;
                edited?: boolean;
              }[];
              attachments: {
                id: string;
                fileName: string;
                fileSize: number;
                contentType: string;
                commentId: string | null;
                uploadedById: string | null;
                createdAt: string;
              }[];
              approvers: WorkItemApprover[];
              watchers: CustomerWatcher[];
              history: CustomerHistoryEntry[];
            })
          | null;
        accessDenied?: boolean;
        message?: string;
      },
      { portalKey: string; itemKey: string }
    >({
      query: ({ portalKey, itemKey }) => `/customer/portals/${portalKey}/requests/${itemKey}`,
      providesTags: (_result, _err, { portalKey, itemKey }) => [
        { type: "WorkItem", id: `CUSTOMER_DETAIL_${portalKey}_${itemKey}` },
      ],
    }),

    addCustomerComment: builder.mutation<
      {
        data: {
          id: string;
          body: string;
          authorName: string | null;
          authorEmail: string | null;
          createdAt: string;
        };
      },
      {
        portalKey: string;
        itemKey: string;
        body: string;
        htmlBody?: string;
        attachmentIds?: string[];
      }
    >({
      query: ({ portalKey, itemKey, body, htmlBody, attachmentIds }) => ({
        url: `/customer/portals/${portalKey}/requests/${itemKey}/comments`,
        method: "POST",
        body: { body, htmlBody, attachmentIds },
      }),
      invalidatesTags: (_result, _err, { portalKey, itemKey }) => [
        { type: "WorkItem", id: `CUSTOMER_DETAIL_${portalKey}_${itemKey}` },
      ],
    }),

    respondCustomerApproval: builder.mutation<
      { data: WorkItemApprover },
      { portalKey: string; itemKey: string; status: "approved" | "rejected" }
    >({
      query: ({ portalKey, itemKey, status }) => ({
        url: `/customer/portals/${portalKey}/requests/${itemKey}/approvers/respond`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_result, _err, { portalKey, itemKey }) => [
        { type: "WorkItem", id: `CUSTOMER_DETAIL_${portalKey}_${itemKey}` },
        { type: "Approval", id: "CUSTOMER_APPROVAL_LIST" },
      ],
    }),

    // ──────────────── Portal Members & Watchers ────────────────

    getCustomerPortalMembers: builder.query<
      { data: CustomerPortalApprover[] },
      { portalKey: string }
    >({
      query: ({ portalKey }) => `/customer/portals/${portalKey}/members`,
      providesTags: (_result, _err, { portalKey }) => [
        { type: "PortalMember", id: `CUSTOMER_MEMBERS_${portalKey}` },
      ],
    }),

    addCustomerWatcher: builder.mutation<
      { success: boolean },
      { portalKey: string; itemKey: string; agentId: string }
    >({
      query: ({ portalKey, itemKey, agentId }) => ({
        url: `/customer/portals/${portalKey}/requests/${itemKey}/watchers`,
        method: "POST",
        body: { agentId },
      }),
      invalidatesTags: (_result, _err, { portalKey, itemKey }) => [
        { type: "WorkItem", id: `CUSTOMER_DETAIL_${portalKey}_${itemKey}` },
      ],
    }),

    removeCustomerWatcher: builder.mutation<
      { success: boolean },
      { portalKey: string; itemKey: string; watcherAgentId: string }
    >({
      query: ({ portalKey, itemKey, watcherAgentId }) => ({
        url: `/customer/portals/${portalKey}/requests/${itemKey}/watchers/${watcherAgentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _err, { portalKey, itemKey }) => [
        { type: "WorkItem", id: `CUSTOMER_DETAIL_${portalKey}_${itemKey}` },
      ],
    }),

    updateCustomerWorkItemComment: builder.mutation<
      { data: { id: string; metadata: Record<string, unknown> } },
      { portalKey: string; itemKey: string; commentId: string; metadata: Record<string, unknown> }
    >({
      query: ({ portalKey, itemKey, commentId, metadata }) => ({
        url: `/customer/portals/${portalKey}/requests/${itemKey}/comments/${commentId}`,
        method: "PATCH",
        body: { metadata },
      }),
      invalidatesTags: (_result, _err, { portalKey, itemKey }) => [
        { type: "WorkItem", id: `CUSTOMER_DETAIL_${portalKey}_${itemKey}` },
      ],
    }),

    updateCustomerTicket: builder.mutation<
      { data: { id: string } },
      {
        portalKey: string;
        itemKey: string;
        title?: string;
        description?: string | null;
      }
    >({
      query: ({ portalKey, itemKey, title, description }) => ({
        url: `/customer/portals/${portalKey}/requests/${itemKey}`,
        method: "PATCH",
        body: { title, description },
      }),
      invalidatesTags: (_result, _err, { portalKey, itemKey }) => [
        { type: "WorkItem", id: `CUSTOMER_DETAIL_${portalKey}_${itemKey}` },
      ],
    }),

    updateCustomerCommentBody: builder.mutation<
      { data: { id: string; body: string; html_body: string | null; updated_at: string } },
      {
        portalKey: string;
        itemKey: string;
        commentId: string;
        body: string;
        htmlBody?: string | null;
      }
    >({
      query: ({ portalKey, itemKey, commentId, body, htmlBody }) => ({
        url: `/customer/portals/${portalKey}/requests/${itemKey}/comments/${commentId}`,
        method: "PATCH",
        body: { body, htmlBody },
      }),
      invalidatesTags: (_result, _err, { portalKey, itemKey }) => [
        { type: "WorkItem", id: `CUSTOMER_DETAIL_${portalKey}_${itemKey}` },
      ],
    }),

    deleteCustomerComment: builder.mutation<
      void,
      { portalKey: string; itemKey: string; commentId: string }
    >({
      query: ({ portalKey, itemKey, commentId }) => ({
        url: `/customer/portals/${portalKey}/requests/${itemKey}/comments/${commentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _err, { portalKey, itemKey }) => [
        { type: "WorkItem", id: `CUSTOMER_DETAIL_${portalKey}_${itemKey}` },
      ],
    }),

    submitCustomerFormResponse: builder.mutation<
      { data: { id: string } },
      {
        formId: string;
        answers: { fieldId: string; value: string }[];
        ticketKey?: string;
        workItemId?: string;
        requestTypeId?: string;
        attachmentIds?: string[];
      }
    >({
      query: ({ formId, answers, ticketKey, workItemId, requestTypeId, attachmentIds }) => ({
        url: `/forms/${formId}/responses`,
        method: "POST",
        body: { answers, ticketKey, workItemId, requestTypeId, attachmentIds },
      }),
      invalidatesTags: (_res, _err, { workItemId }) =>
        workItemId ? [{ type: "WorkItem", id: workItemId }] : [],
    }),
  }),
});

export const {
  useGetCustomerPortalsQuery,
  useGetCustomerPortalQuery,
  useGetCustomerPortalGroupsQuery,
  useGetCustomerRequestTypesQuery,
  useGetCustomerCustomFieldsQuery,
  useGetCustomerRequestsQuery,
  useGetAllCustomerRequestsQuery,
  useGetApprovalRequestsQuery,
  useGetCustomerPortalApproversQuery,
  useUploadCustomerAttachmentMutation,
  useDeleteCustomerAttachmentMutation,
  useDeleteCustomerTicketAttachmentMutation,
  useCreateCustomerRequestMutation,
  useSetWorkItemApproversMutation,
  useGetPortalBrandingQuery,
  useUpdatePortalBrandingMutation,
  useUploadPortalBrandingLogoMutation,
  useDeletePortalBrandingLogoMutation,
  // Request Detail & Comments
  useGetCustomerRequestDetailQuery,
  useAddCustomerCommentMutation,
  useRespondCustomerApprovalMutation,
  // Portal Members & Watchers
  useGetCustomerPortalMembersQuery,
  useAddCustomerWatcherMutation,
  useRemoveCustomerWatcherMutation,
  // Form in chat
  useUpdateCustomerWorkItemCommentMutation,
  useUpdateCustomerTicketMutation,
  useUpdateCustomerCommentBodyMutation,
  useDeleteCustomerCommentMutation,
  useSubmitCustomerFormResponseMutation,
} = customerPortalApi;
